import { api } from '@src/lib/api-client';
import { QueryConfig } from '@src/lib/react-query';
import { GetCobrandResponse } from '@src/types/get-cobrand-response';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { getCookie, ToastUtility } from '@utils';

export type CobrandParams = {
  login?: boolean;
  partner?: string;
};

export async function getCobrand({
  login,
  partner,
}: CobrandParams): Promise<GetCobrandResponse | null> {
  const params = new URLSearchParams();
  login && params.append('login', login.toString());
  partner?.length && params.append('partner', partner);

  const { data: cobrandData } = await api.get<GetCobrandResponse>(
    'api/v3/settings/cobrand',
    { params },
  );

  return {
    ...cobrandData,
    image: {
      ...cobrandData?.image,
      url: cobrandData?.image?.url
        ? import.meta.env.VITE_BASE_URL + cobrandData.image.url
        : '',
    },
  };
}

export const getCobrandQueryOptions = (params: CobrandParams) => {
  // Normalize parameters to ensure consistent query keys
  const normalizedParams = {
    login: params.login ?? false,
    partner: params.partner ?? '',
  };

  const queryKey = ['cobrand'];
  if (params.partner) {
    queryKey.push(params.partner);
  }
  if (params.login) {
    queryKey.push('login');
  }

  return queryOptions({
    queryKey,
    queryFn: () => getCobrand(normalizedParams),
    throwOnError: false,
  });
};

type UseGetCobrandOptions = {
  login?: boolean;
  queryConfig?: QueryConfig<typeof getCobrandQueryOptions>;
};

export const useGetCobrand = ({
  login,
  queryConfig,
}: UseGetCobrandOptions = {}) => {
  const partner = getCookie('partner');
  const params = { login: login ?? false, partner: partner ?? '' };

  return useQuery({
    ...getCobrandQueryOptions(params),
    ...queryConfig,
  });
};

export const callCobrandErrorToast = () =>
  ToastUtility.error('There was an error while getting the cobrand logo', {
    toastId: 'cobrand',
  });
